import { useContext, useEffect, useRef } from "react";

import api from "../services/api";
import { AuthContext } from "../context/Auth/AuthContext";
import ColorModeContext from "../layout/themeContext";
import { useSoundPreferences, updateSoundPreferences } from "../utils/soundPreferences";

const toPayload = (theme, sound) => ({
  theme,
  sound: {
    volume: Math.round(Number(sound.volume) * 100) / 100,
    muted: Boolean(sound.muted),
    tickets: Boolean(sound.tickets),
    chat: Boolean(sound.chat),
  },
});

// Tema e sons do menu da conta guardados no usuário, para valerem em qualquer
// computador:
// 1. ao entrar, aplica o que está salvo no servidor (se houver);
// 2. depois, cada mudança feita no menu é enviada (com 1 s de espera, para
//    não mandar uma requisição por passo do controle de volume).
// O navegador continua guardando uma cópia, então tudo funciona mesmo se o
// servidor não responder.
const usePreferencesSync = () => {
  const { user } = useContext(AuthContext);
  const { colorMode } = useContext(ColorModeContext);
  const [sound] = useSoundPreferences();
  const appliedFor = useRef(null);
  const lastSent = useRef(null);

  const userId = user?.id;
  const theme = colorMode?.themePreference;

  // 1. Aplica as preferências do servidor uma vez por login.
  useEffect(() => {
    if (!userId) {
      appliedFor.current = null;
      lastSent.current = null;
      return;
    }
    if (appliedFor.current === userId) return;
    appliedFor.current = userId;

    const saved = user?.preferences;
    if (saved?.theme) colorMode.setThemePreference(saved.theme);
    if (saved?.sound) updateSoundPreferences(saved.sound);
    lastSent.current = saved?.theme && saved?.sound
      ? JSON.stringify(toPayload(saved.theme, saved.sound))
      : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // 2. Envia as mudanças locais.
  useEffect(() => {
    if (!userId || appliedFor.current !== userId || !theme) return;
    const payload = toPayload(theme, sound);
    const key = JSON.stringify(payload);
    if (key === lastSent.current) return;

    const timer = setTimeout(() => {
      api
        .put("/users/me/preferences", payload)
        .then(() => {
          lastSent.current = key;
        })
        .catch(() => {
          // Sem conexão ou sessão encerrada: fica só no navegador por ora.
        });
    }, 1000);
    return () => clearTimeout(timer);
  }, [userId, theme, sound]);
};

export default usePreferencesSync;
